/**
 * "Max ctr duration" — the longest a counter may run before auto-assign owes
 * the officer a break.
 *
 * One value for the whole app: it is set from whichever header the user
 * happens to be in front of (Add Officers, or step 4 of the wizard), every
 * auto-assign reads the same one, and it is remembered between sessions so a
 * unit that never works to 2 h 30 sets its own figure once instead of on every
 * add. An officer whose window is shorter than the cap simply works it through
 * without a break — there is nothing to break up.
 *
 * The value is HHMM, the way the field shows it. The backend validates the
 * same range it is written in (api/models.py: max_counter_duration_slots).
 */

import { useEffect, useState } from 'react';

/** 2 h 30 — the cap the roster used before it was the user's to set. */
export const DEFAULT_MAX_COUNTER_DURATION = '0230';

/** Minutes the field offers: quarter-hours only, matching every other time in
 *  the roster. */
export const MAX_COUNTER_MINUTE_OPTIONS = ['00', '15', '30', '45'] as const;

/** Hours the field offers: 1 to 11. Zero is not a counter, and 12 is the shift. */
export const MAX_COUNTER_HOUR_OPTIONS = Array.from(
  { length: 11 },
  (_, i) => String(i + 1).padStart(2, '0'),
);

const STORAGE_KEY = 'ac_max_counter_duration';

export function isValidMaxCounterDuration(hhmm: string): boolean {
  if (!/^\d{4}$/.test(hhmm)) return false;
  const hours = parseInt(hhmm.slice(0, 2), 10);
  return hours >= 1 && hours <= 11 && (MAX_COUNTER_MINUTE_OPTIONS as readonly string[]).includes(hhmm.slice(2));
}

/** "0230" → "02:30", for reading rather than editing. */
export function formatMaxCounterDuration(hhmm: string): string {
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

let current: string = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidMaxCounterDuration(stored)) return stored;
  } catch { /* private mode / storage disabled — the default stands */ }
  return DEFAULT_MAX_COUNTER_DURATION;
})();

const listeners = new Set<(value: string) => void>();

/** The current cap. Read by the API client on every request that can trigger
 *  an automatic break assignment, so no call site can forget to send it. */
export function getMaxCounterDuration(): string {
  return current;
}

export function setMaxCounterDuration(hhmm: string): void {
  if (!isValidMaxCounterDuration(hhmm) || hhmm === current) return;
  current = hhmm;
  try {
    localStorage.setItem(STORAGE_KEY, hhmm);
  } catch { /* not remembering it is better than not applying it */ }
  listeners.forEach((listener) => listener(hhmm));
}

/** The cap, live: every field showing it updates when any one of them changes. */
export function useMaxCounterDuration(): [string, (hhmm: string) => void] {
  const [value, setValue] = useState(current);
  useEffect(() => {
    listeners.add(setValue);
    setValue(current);
    return () => { listeners.delete(setValue); };
  }, []);
  return [value, setMaxCounterDuration];
}
